/**
 * Goods Receipt List Page
 * Epic 21: Bevételezés lista + összesítő kártyák + avizó tab
 *
 * Features:
 *   - Receipts table with status filter and search
 *   - Stats cards: total, in-progress, discrepancies, completed this month
 *   - "Avizók" tab showing pending purchase advices
 *   - "Új bevételezés" button → form page
 */

import type { Avizo, Receipt, ReceiptStatus } from '@/api/goods-receipt';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { usePendingAvizos } from '@/hooks/useGoodsReceipt';
import { useTranslation } from '@kgc/i18n';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '@/api/client';

type TabKey = 'receipts' | 'avizos';

const STATUS_COLORS: Record<ReceiptStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  IN_PROGRESS: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
  DISCREPANCY: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
};

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('hu-HU');
}

export function GoodsReceiptListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('receipts');
  const [statusFilter, setStatusFilter] = useState<ReceiptStatus | ''>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch receipts — the backend doesn't have a list endpoint in the controller,
  // so we use a generic GET with query params. If the endpoint is not yet available,
  // the hook gracefully returns an empty array.
  const receiptsQuery = useQuery({
    queryKey: ['goods-receipt', 'receipts', 'list', { status: statusFilter, search: searchTerm }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (searchTerm) params.set('search', searchTerm);
      const query = params.toString();
      const res = await api.get<{ data: Receipt[] }>(`/receipts${query ? `?${query}` : ''}`);
      return res.data;
    },
  });

  const receipts = useMemo(() => (receiptsQuery.data ?? []) as Receipt[], [receiptsQuery.data]);
  const isLoading = receiptsQuery.isLoading;

  // Avizos
  const { avizos, isLoading: avizosLoading } = usePendingAvizos();

  // Stats
  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      total: receipts.length,
      inProgress: receipts.filter(r => r.status === 'IN_PROGRESS' || r.status === 'DRAFT').length,
      withDiscrepancy: receipts.filter(r => r.status === 'DISCREPANCY').length,
      completedThisMonth: receipts.filter(
        r => r.status === 'COMPLETED' && r.completedAt && new Date(r.completedAt) >= monthStart
      ).length,
    };
  }, [receipts]);

  const hasDiscrepancy = (receipt: Receipt): boolean => {
    // `items` is only populated by the detail endpoint; on the list
    // response it's absent. Without items we can't compute discrepancy
    // client-side — fall back to the server-provided `status` field.
    return (
      receipt.items?.some(item => item.expectedQuantity !== item.receivedQuantity) ??
      receipt.status === 'DISCREPANCY'
    );
  };

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="bg-white dark:bg-[#1a4f5c] shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">{t('goodsReceipt.title')}</h1>
              <p className="text-sm text-muted-foreground">{t('goodsReceipt.subtitle')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/goods-receipt/new')}
            className="bg-primary hover:bg-primary/90"
          >
            {t('goodsReceipt.newReceipt')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats Cards */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-muted-foreground">
                {t('goodsReceipt.stats.total')}
              </p>
              <p className="mt-1 text-2xl font-bold text-foreground">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-muted-foreground">
                {t('goodsReceipt.stats.inProgress')}
              </p>
              <p className="mt-1 text-2xl font-bold text-blue-600 dark:text-blue-400">
                {stats.inProgress}
              </p>
            </CardContent>
          </Card>
          <Card
            className={
              stats.withDiscrepancy > 0
                ? 'border-orange-300 bg-orange-50 dark:border-orange-600 dark:bg-orange-900/20'
                : ''
            }
          >
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-muted-foreground">
                {t('goodsReceipt.stats.withDiscrepancy')}
              </p>
              <p
                className={`mt-1 text-2xl font-bold ${stats.withDiscrepancy > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-foreground'}`}
              >
                {stats.withDiscrepancy}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-muted-foreground">
                {t('goodsReceipt.stats.completedThisMonth')}
              </p>
              <p className="mt-1 text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.completedThisMonth}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
          <button
            onClick={() => setActiveTab('receipts')}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'receipts'
                ? 'bg-white text-foreground shadow dark:bg-[#1a4f5c]'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('goodsReceipt.tabs.receipts')} ({receipts.length})
          </button>
          <button
            onClick={() => setActiveTab('avizos')}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'avizos'
                ? 'bg-white text-foreground shadow dark:bg-[#1a4f5c]'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('goodsReceipt.tabs.avizos')} ({avizos.length})
          </button>
        </div>

        {/* Receipts Tab */}
        {activeTab === 'receipts' && (
          <>
            {/* Filters */}
            <div className="mb-6 flex flex-wrap gap-4">
              <Input
                type="text"
                placeholder={t('goodsReceipt.searchPlaceholder')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-64"
              />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as ReceiptStatus | '')}
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-foreground px-3 py-2"
              >
                <option value="">{t('goodsReceipt.allStatuses')}</option>
                <option value="DRAFT">{t('goodsReceipt.statusLabels.draft')}</option>
                <option value="IN_PROGRESS">{t('goodsReceipt.statusLabels.inProgress')}</option>
                <option value="DISCREPANCY">{t('goodsReceipt.statusLabels.discrepancy')}</option>
                <option value="COMPLETED">{t('goodsReceipt.statusLabels.completed')}</option>
              </select>
            </div>

            {/* Loading */}
            {isLoading && (
              <Card>
                <CardContent className="py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('common.loading')}</p>
                </CardContent>
              </Card>
            )}

            {/* Table */}
            {!isLoading && (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800 text-left text-sm text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3 font-medium">
                          {t('goodsReceipt.table.receiptNumber')}
                        </th>
                        <th className="px-4 py-3 font-medium">
                          {t('goodsReceipt.table.supplier')}
                        </th>
                        <th className="px-4 py-3 font-medium">
                          {t('goodsReceipt.table.receivedDate')}
                        </th>
                        <th className="px-4 py-3 font-medium">{t('table.status')}</th>
                        <th className="px-4 py-3 font-medium text-right">
                          {t('goodsReceipt.table.itemCount')}
                        </th>
                        <th className="px-4 py-3 font-medium text-center">
                          {t('goodsReceipt.table.discrepancy')}
                        </th>
                        <th className="px-4 py-3 font-medium" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {receipts.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                            {t('goodsReceipt.noReceipts')}
                          </td>
                        </tr>
                      ) : (
                        receipts.map(receipt => (
                          <tr
                            key={receipt.id}
                            className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                            onClick={() => navigate(`/goods-receipt/${receipt.id}`)}
                          >
                            <td className="px-4 py-3">
                              <p className="font-medium text-foreground">{receipt.receiptNumber}</p>
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">
                              {receipt.supplierName}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {formatDate(receipt.createdAt)}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_COLORS[receipt.status]}`}
                              >
                                {t(
                                  `goodsReceipt.statusLabels.${receipt.status === 'IN_PROGRESS' ? 'inProgress' : receipt.status.toLowerCase()}`
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-foreground">
                              {receipt.items?.length ?? '—'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {hasDiscrepancy(receipt) ? (
                                <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900/20 dark:text-orange-300">
                                  {t('goodsReceipt.hasDiscrepancy')}
                                </span>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={e => {
                                  e.stopPropagation();
                                  navigate(`/goods-receipt/${receipt.id}`);
                                }}
                              >
                                {t('common.details')}
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}

        {/* Avizos Tab */}
        {activeTab === 'avizos' && (
          <>
            {avizosLoading ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('common.loading')}</p>
                </CardContent>
              </Card>
            ) : avizos.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  {t('goodsReceipt.noAvizos')}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800 text-left text-sm text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3 font-medium">
                          {t('goodsReceipt.avizo.supplier')}
                        </th>
                        <th className="px-4 py-3 font-medium">
                          {t('goodsReceipt.avizo.expectedDate')}
                        </th>
                        <th className="px-4 py-3 font-medium text-right">
                          {t('goodsReceipt.avizo.itemCount')}
                        </th>
                        <th className="px-4 py-3 font-medium">{t('goodsReceipt.avizo.notes')}</th>
                        <th className="px-4 py-3 font-medium" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {avizos.map((avizo: Avizo) => (
                        <tr key={avizo.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-4 py-3">
                            <p className="font-medium text-foreground">{avizo.supplierName}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {formatDate(avizo.expectedDate)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-foreground">
                            {avizo.items.length}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {avizo.notes ?? '-'}
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/goods-receipt/new?avizoId=${avizo.id}`)}
                            >
                              {t('goodsReceipt.avizo.startReceipt')}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
